import { FC, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { TripPageDocument } from '../../graphql/generated/graphql';
import { useFetchGeneric } from '../../lib/hooks/useGenericFetch';
import { useEnvConfig } from '../../providers/ConfigProvider';
import { renderBookingPdf } from '../booking/renderBookingPdf';
import { tripToBookingPdfData } from '../booking/tripToBookingPdfData';

// In-browser preview route for the booking PDF. Renders the PDF to a Blob and
// streams it through an iframe so the browser's native viewer handles paging
// and download — same UX we had with @react-pdf/renderer's PDFViewer.
const TripPdfPreview: FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const envConfig = useEnvConfig();
  const { data, loading } = useFetchGeneric(TripPageDocument, { id });
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const currentUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!data?.trip) {
      return;
    }
    let cancelled = false;
    const generate = async () => {
      const adapted = await tripToBookingPdfData(data.trip!, envConfig);
      const bytes = await renderBookingPdf(adapted);
      if (cancelled) {
        return;
      }
      const blob = new Blob([bytes as BlobPart], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      if (currentUrlRef.current) {
        URL.revokeObjectURL(currentUrlRef.current);
      }
      currentUrlRef.current = url;
      setPdfUrl(url);
    };
    generate();
    return () => {
      cancelled = true;
    };
  }, [data?.trip, envConfig]);

  useEffect(
    () => () => {
      if (currentUrlRef.current) {
        URL.revokeObjectURL(currentUrlRef.current);
      }
    },
    [],
  );

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!pdfUrl) {
    return <div>Preparing preview…</div>;
  }

  return (
    <div style={{ height: '100vh', width: '100%' }}>
      <iframe
        title="Trip PDF preview"
        src={`${pdfUrl}#toolbar=1&view=FitH`}
        style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
      />
    </div>
  );
};

export default TripPdfPreview;
